<script lang="ts" setup>
import config from '@/config.json';
import { computed, onBeforeMount } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { Quiz } from '@/models/Quiz';
import { QuizAnswer } from '@/models/QuizAnswer';
import { progress } from '@/composables/progress';

const router = useRouter();
const route = useRoute();
const props = defineProps<{
  quizzes: Array<Quiz>,
}>();

const emit = defineEmits<{
  (e: 'selectAnswer', value: {
    quizId: string | undefined,
    questionIndex: number,
    answerIndex: number
  }): void,
  (e: 'updateScore', value: Quiz): void,
}>();

onBeforeMount (() => {
  const questionIndex = parseInt(route.params.questionIndex as string);
  const quiz = props.quizzes.find((quiz: Quiz) => quiz.id === route.params.id);

  if (!quiz || typeof quiz?.questions[questionIndex] === 'undefined') {
    router.replace({ name: 'home' });
  }

  if (quiz && config.shuffleAnswers) {
    shuffleAnswers(quiz);
  }
});

const quiz = computed(() => {
  return props.quizzes.find((quiz: Quiz) => quiz.id === route.params.id);
});

const questionIndex = computed(() => {
  return parseInt(route.params.questionIndex as string);
});

const questionText = computed(() => {
  return quiz.value?.questions
      ? quiz?.value.questions[questionIndex.value]
          ?.text.replace('___', '<span class="gap"></span>')
      : 'Frage nicht gefunden'
    ;
});

const hasQuestion = computed(() => {
  return quiz.value?.questions[questionIndex.value];
});

const hasNextQuestion = computed(() => {
  return quiz.value?.questions[questionIndex.value + 1];
});

const answers = computed(() => {
  return quiz.value?.questions[questionIndex.value]?.answers;
});

const image = computed(() => {
  return quiz.value?.questions[questionIndex.value]?.image;
});

const hasImage = computed(() => {
  return quiz.value?.questions[questionIndex.value]?.image;
});

const altText = computed(() => {
  return quiz.value?.questions[questionIndex.value]?.altText || '';
});

const hasCode = computed(() => {
  return quiz.value?.questions[questionIndex.value]?.code;
});

const codeLanguage = computed(() => {
  return hasCode.value ? 'language-' + quiz.value?.questions[questionIndex.value].code.language : '';
});

const isSingleChoice = computed(() => {
  return hasQuestion.value
    && answers.value
    && quiz.value?.questions[questionIndex.value].answers
      .filter((answer: QuizAnswer) => answer.correct)
      .length === 1;
});

const code = computed(() => {
  let code = '';

  quiz.value?.questions[questionIndex.value].code?.lines
    .forEach((line: string) => {
      code += `<span class="dive-code">${ line }</span><br />`
    });

  return code;
});

const selectAnswer = (index: number) => {
  emit('selectAnswer', {
    quizId: quiz.value?.id,
    questionIndex: questionIndex.value,
    answerIndex: index,
  });
};

const isAnswered = (): boolean => {
  const answered = answers.value?.filter((answer: QuizAnswer) => answer.selected);

  return answered ? answered.length > 0 : false;
};

const nextQuestion = async () => {
  await router.push({
    name: 'quiz',
    params: {
      id: quiz.value?.id,
      questionIndex: questionIndex.value + 1,
    },
  });

  document.getElementById('quiz')?.focus();
};

const showResult = async () => {
  if (quiz.value) {
    quiz.value.completed = true;

    emit('updateScore', quiz.value);

    await router.push({
      name: 'result',
      params: {
        id: quiz.value.id,
        score: Math.round(quiz.value.score),
      },
    });

    document.getElementById('quiz-result')?.focus();
  }
}

const shuffleAnswers = (quiz: Quiz) => {
  if (!quiz) {
    return;
  }

  quiz.questions.forEach((question) => {
    const answers = question.answers;
    let currentIndex = answers.length, randomIndex: number;

    while (currentIndex !== 0) {
      randomIndex = Math.floor(Math.random() * currentIndex);
      currentIndex--;

      [answers[currentIndex], answers[randomIndex]] = [answers[randomIndex], answers[currentIndex]];
    }
  });
}
</script>


<template>
  <section id="quiz" tabindex="-1" aria-label="Quiz">
    <div class="row">
      <div class="column">
        <div class="quiz" v-if="quiz">
          <div class="question" v-if="hasQuestion">
            <div class="question__title" v-if="questionText">
              <p class="question__type">
                <span v-if="isSingleChoice">Single Choice</span>
                <span v-else>Multiple Choice</span>
              </p>
              <h3 v-html="questionText"></h3>
            </div>
            <div class="question__code" v-if="hasCode">
              <pre v-highlightjs :class="codeLanguage">
                <code class="javascript" v-html="code"></code>
              </pre>
            </div>
            <div class="question__image" v-if="hasImage">
              <img :src="require(`@/assets/quiz/images/${quiz.id}/${image}`)" :alt="altText" />
            </div>
            <div
              class="question__answer"
              :class="{ selected: answer.selected, singleChoice: isSingleChoice }"
              :aria-selected="answer.selected"
              v-on:click="selectAnswer(answerIndex)"
              v-on:keyup.enter.space="selectAnswer(answerIndex)"
              v-bind:key="answerIndex"
              v-for="(answer, answerIndex) in answers"
              tabIndex="0"
              role="button"
            >
              {{ answer.text }}
            </div>
            <div class="question__footer">
              <div class="actions">
                <button
                  v-if="hasNextQuestion"
                  v-on:click="nextQuestion()"
                  :disabled="!isAnswered()"
                >
                  Nächste Frage
                </button>
                <button
                  v-if="!hasNextQuestion"
                  v-on:click="showResult()"
                   :disabled="!isAnswered()"
                >
                  <span v-if="!isAnswered()">Antworten wählen</span>
                  <span v-else>Zur Auswertung</span>
                </button>
              </div>
              <div class="meta" v-if="quiz.questions">
                <small>
                  <strong>{{ questionIndex + 1 }}</strong> von <strong>{{ quiz.questions.length }}</strong>
                </small>
              </div>
              <div class="progress-bar" :style="{ 'width': progress(quiz) + '%' }"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>


<style lang="scss" scoped>
@import '@/assets/scss/quiz.scss';

.row .column {
  padding-top: 3em;
  padding-bottom: 3em;
}

.progress-bar {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 0.875em;
  width: 100%;
  background: $color-primary-400;
}
</style>
